/**
 * useAlerts Hook - React hooks for alert management
 * Provides easy access to alert data and real-time updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import alertService from '../services/alertService';
import { shouldPlaySound, getNotificationSound } from '../utils/alertHelpers';

/**
 * Hook for managing alerts with real-time updates
 * @param {Object} options - Hook options
 * @param {boolean} options.autoFetch - Automatically fetch on mount
 * @param {boolean} options.realtime - Enable real-time updates
 * @param {boolean} options.unreadOnly - Only fetch unread alerts
 * @param {number} options.limit - Max alerts to fetch
 */
export function useAlerts({
  autoFetch = true,
  realtime = true,
  unreadOnly = false,
  limit = 50
} = {}) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const subscriptionRef = useRef(null);

  // Fetch alerts
  const fetchAlerts = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await alertService.getAlerts({
        unreadOnly,
        limit,
        ...options
      });
      setAlerts(result.alerts);
      setTotalCount(result.count);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [unreadOnly, limit]);

  // Mark single alert as read
  const markAsRead = useCallback(async (alertId) => {
    try {
      await alertService.markAsRead(alertId);
      setAlerts(prev =>
        prev.map(alert =>
          alert.id === alertId
            ? { ...alert, is_read: true, read_at: new Date().toISOString() }
            : alert
        )
      );
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, []);

  // Mark all alerts as read
  const markAllAsRead = useCallback(async () => {
    try {
      const count = await alertService.markAllAsRead();
      setAlerts(prev =>
        prev.map(alert => ({
          ...alert,
          is_read: true,
          read_at: new Date().toISOString()
        }))
      );
      return count;
    } catch (err) {
      setError(err.message);
      return 0;
    }
  }, []);

  // Dismiss an alert
  const dismissAlert = useCallback(async (alertId) => {
    try {
      await alertService.dismissAlert(alertId);
      setAlerts(prev => prev.filter(alert => alert.id !== alertId));
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, []);

  // Handle real-time updates
  useEffect(() => {
    if (autoFetch) {
      fetchAlerts();
    }

    if (realtime) {
      subscriptionRef.current = alertService.subscribeToAlerts((newAlert) => {
        setAlerts(prev => [newAlert, ...prev]);
        setTotalCount(prev => prev + 1);
      });
    }

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [autoFetch, realtime, fetchAlerts]);

  return {
    alerts,
    loading,
    error,
    totalCount,
    fetchAlerts,
    markAsRead,
    markAllAsRead,
    dismissAlert,
    refresh: fetchAlerts
  };
}

/**
 * Hook for just the unread count with real-time updates
 * Lightweight hook for notification badges
 */
export function useUnreadCount() {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const subscriptionRef = useRef(null);

  // Fetch initial count
  const fetchCount = useCallback(async () => {
    try {
      const unreadCount = await alertService.getUnreadCount();
      setCount(unreadCount);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Increment count (for new alerts)
  const incrementCount = useCallback(() => {
    setCount(prev => prev + 1);
  }, []);

  // Decrement count (when marking as read)
  const decrementCount = useCallback((amount = 1) => {
    setCount(prev => Math.max(0, prev - amount));
  }, []);

  // Reset count to 0
  const resetCount = useCallback(() => {
    setCount(0);
  }, []);

  useEffect(() => {
    fetchCount();

    // Subscribe to new alerts
    subscriptionRef.current = alertService.subscribeToAlerts(() => {
      incrementCount();
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [fetchCount, incrementCount]);

  return {
    count,
    loading,
    refresh: fetchCount,
    incrementCount,
    decrementCount,
    resetCount
  };
}

/**
 * Hook for alert preferences management
 */
export function useAlertPreferences() {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch preferences
  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const prefs = await alertService.getPreferences();
      setPreferences(prefs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Update preferences
  const updatePreferences = useCallback(async (updates) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await alertService.updatePreferences(updates);
      setPreferences(updated);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Toggle a boolean preference
  const togglePreference = useCallback(async (key) => {
    if (!preferences) return false;
    return updatePreferences({ [key]: !preferences[key] });
  }, [preferences, updatePreferences]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  return {
    preferences,
    loading,
    saving,
    error,
    updatePreferences,
    togglePreference,
    refresh: fetchPreferences
  };
}

/**
 * Hook for alert statistics
 */
export function useAlertStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await alertService.getStatistics();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refresh: fetchStats };
}

/**
 * Hook for real-time alerts with sound notifications
 * Enhanced version with delivery tracking and sound support
 * @param {Object} options - Hook options
 * @param {Function} options.onNewAlert - Callback when new alert arrives
 * @param {boolean} options.playSound - Whether to play notification sounds
 * @param {boolean} options.showBrowserNotification - Whether to show browser notifications
 */
export function useRealtimeAlerts({
  onNewAlert = null,
  playSound = true,
  showBrowserNotification = false
} = {}) {
  const [latestAlert, setLatestAlert] = useState(null);
  const [alertQueue, setAlertQueue] = useState([]);
  const subscriptionRef = useRef(null);
  const audioRef = useRef(null);

  // Play notification sound
  const playNotificationSound = useCallback((alert) => {
    if (!playSound || !shouldPlaySound(alert)) return;

    const soundUrl = getNotificationSound(alert.priority);
    if (!soundUrl) return;

    try {
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      audioRef.current.src = soundUrl;
      audioRef.current.play().catch(() => {
        // Ignore autoplay errors
      });
    } catch (err) {
      console.warn('Could not play notification sound:', err);
    }
  }, [playSound]);

  // Show browser notification
  const showNotification = useCallback(async (alert) => {
    if (!showBrowserNotification) return;
    if (!('Notification' in window)) return;

    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }

    if (Notification.permission === 'granted') {
      new Notification(alert.title || 'New Alert', {
        body: alert.description || '',
        icon: '/favicon.ico',
        tag: alert.id
      });
    }
  }, [showBrowserNotification]);

  // Handle new alert
  const handleNewAlert = useCallback((alert) => {
    setLatestAlert(alert);
    setAlertQueue(prev => [alert, ...prev].slice(0, 10)); // Keep last 10

    // Mark as delivered
    alertService.markAlertsDelivered([alert.id]).catch(console.error);

    // Play sound
    playNotificationSound(alert);

    // Show browser notification
    showNotification(alert);

    // Call custom callback
    if (onNewAlert) {
      onNewAlert(alert);
    }
  }, [onNewAlert, playNotificationSound, showNotification]);

  // Clear alert queue
  const clearQueue = useCallback(() => {
    setAlertQueue([]);
    setLatestAlert(null);
  }, []);

  useEffect(() => {
    subscriptionRef.current = alertService.subscribeToAlerts(handleNewAlert);

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [handleNewAlert]);

  return {
    latestAlert,
    alertQueue,
    clearQueue
  };
}

/**
 * Hook for tracking alert clicks
 * Handles click tracking and navigation
 */
export function useAlertClick() {
  const [clicking, setClicking] = useState(false);

  const trackAndNavigate = useCallback(async (alert, navigate) => {
    if (clicking) return;

    setClicking(true);
    try {
      // Track the click
      await alertService.trackClick(alert.id);

      // Navigate if URL provided
      if (alert.action_url && navigate) {
        navigate(alert.action_url);
      }
    } catch (err) {
      console.error('Error tracking alert click:', err);
    } finally {
      setClicking(false);
    }
  }, [clicking]);

  return { trackAndNavigate, clicking };
}

/**
 * Hook for user engagement metrics
 * @param {string} userId - User ID (optional, uses current user if not provided)
 */
export function useEngagementMetrics(userId = null) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await alertService.getEngagementMetrics(userId);
      setMetrics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchMetrics();
    }
  }, [userId, fetchMetrics]);

  return { metrics, loading, error, refresh: fetchMetrics };
}

/**
 * Hook for checking quiet hours status
 * @param {string} userId - User ID (optional)
 */
export function useQuietHours(userId = null) {
  const [isQuietHours, setIsQuietHours] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkQuietHours = useCallback(async () => {
    setLoading(true);
    try {
      const result = await alertService.isInQuietHours(userId);
      setIsQuietHours(result);
    } catch (err) {
      console.error('Error checking quiet hours:', err);
      setIsQuietHours(false);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    checkQuietHours();

    // Re-check every minute
    const interval = setInterval(checkQuietHours, 60000);
    return () => clearInterval(interval);
  }, [checkQuietHours]);

  return { isQuietHours, loading, refresh: checkQuietHours };
}

export default useAlerts;
